import { useNavigate, useSearchParams } from 'react-router-dom'
import { GiftType, PassReqType } from 'src/types/api'

import { animateScroll } from 'react-scroll'
import { useChannel } from 'src/provider/ChannelContextProvider'

const usePageNav = () => {
  const { channel } = useChannel()
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()
  const utmParameter = `${searchParams.toString()}`

  const addUtmParameter = (path: string) => {
    return utmParameter?.length === 0 ? `/${channel}${path}` : `/${channel}${path}/?${utmParameter}`
  }

  const navigatePrev = () => navigate(-1)

  const errorPageNav = (cmdtype: number, errcode: number) =>
    navigate(addUtmParameter(`/error/${cmdtype}/${errcode}`))

  const introPageNav = () => navigate(addUtmParameter(`/intro`))
  const splashPageNav = () => navigate(addUtmParameter(`/splash`))

  const mainPageNav = () => {
    navigate(addUtmParameter(`/main`), { replace: true })
    animateScroll.scrollToTop({ duration: 0, smooth: false })
  }

  const alwaysPageNav = (categoryNumber: number, replace: boolean) =>
    navigate(addUtmParameter(`/always/${categoryNumber}`), { replace: replace })

  const couponPurchaseHistoryPageNav = () => navigate(addUtmParameter(`/coupon/purchase_history`))

  const purchaseHistorySavePageNav = () =>
    navigate(addUtmParameter(`/coupon/purchase_history/save`))

  const purchaseHistoryDetailPageNav = (
    Idx: number,
    couponIdx: number,
    couponStatus: string,
    couponType: number,
    couponNumber: string,
    replace?: boolean,
  ) =>
    navigate(
      addUtmParameter(
        `/coupon/purchase_history/${couponIdx}/${Idx}/${couponStatus}/${couponType}/${couponNumber}`,
      ),
      { replace: replace || false },
    )

  const giftedCouponPageNav = () => navigate(addUtmParameter(`/gift/gifted_coupon`))

  const giftedCouponDetailPageNav = (giftIdx: number) =>
    navigate(addUtmParameter(`/gift/gifted_coupon/${giftIdx}`))

  const couponDetailPageNav = (couponNumber: string, couponIdx: number, couponType: 0 | 1 | 2) =>
    couponIdx !== 0 &&
    navigate(addUtmParameter(`/coupon/detail/${couponType}/${couponIdx}/${couponNumber}`))

  const hotDealDetailPageNav = (couponNumber: string, couponIdx: number) =>
    couponIdx !== 0 && navigate(addUtmParameter(`/coupon/detail/1/${couponIdx}/${couponNumber}`))

  const alwaysCouponDetailPageNav = (couponNumber: string, couponIdx: number) =>
    couponIdx !== 0 && navigate(addUtmParameter(`/coupon/detail/0/${couponIdx}/${couponNumber}`))

  const brandListPageNav = () => navigate(addUtmParameter(`/brand_list`))

  const freeCouponPageNav = () => navigate(addUtmParameter(`/coupon/free_coupon/`))

  const freeCouponDetailPageNav = (idx: number) =>
    navigate(addUtmParameter(`/coupon/free_coupon/${idx}`))

  const joinPageNav = () => {
    navigate(addUtmParameter(`/join/cert`))
  }

  const passFormPageNav = (passReqType: PassReqType, reqNum: string) => {
    navigate(addUtmParameter(`/pass/${passReqType}/${reqNum}`))
  }

  const brandSalePageNav = (categoryNumber: number, brandNumber: number, brandName: string) =>
    navigate(addUtmParameter(`/brand_list/${categoryNumber}/${brandNumber}/0`), {
      state: { brandName: brandName },
    })
  const brandPossessionPageNav = (categoryNumber: number, brandNumber: number, brandName: string) =>
    navigate(addUtmParameter(`/brand_list/${categoryNumber}/${brandNumber}/1`), {
      state: { brandName: brandName },
    })
  const purchasePageNav = (couponType: number, couponIdx: number, couponNumber: string) =>
    navigate(addUtmParameter(`/coupon/purchase/${couponType}/${couponIdx}/${couponNumber}`))

  const giftPurchasePageNav = (couponType: number, couponIdx: number, couponNumber: string) =>
    navigate(addUtmParameter(`/gift/purchase/${couponType}/${couponIdx}/${couponNumber}`))

  const giftSendPurchasedPageNav = (
    purchaseIdx: number,
    couponStatus: string,
    couponNumber: string,
  ) =>
    navigate(addUtmParameter(`/gift/send_purchased/${purchaseIdx}/${couponStatus}/${couponNumber}`))

  const cancelCompletePageNav = () => navigate(addUtmParameter(`/cancel/complete`))

  const refundPageNav = (
    Idx: number,
    couponIdx: number,
    couponNumber: string,
    orderId: string,
    orderDT: string,
  ) =>
    navigate(
      addUtmParameter(
        `/coupon/refund_apply/${Idx}/${couponIdx}/${couponNumber}/${orderId}/${orderDT}`,
      ),
    )

  const selfRegisterUpdatePageNav = (idx: number) =>
    navigate(addUtmParameter(`/coupon/self_register/update/${idx}`))

  const termsPageNav = () => navigate(addUtmParameter(`/help_center/terms`))

  const serviceTermsPageNav = () => navigate(addUtmParameter(`/help_center/terms/service`))

  const privacyPolicyPageNav = () => navigate(addUtmParameter(`/help_center/terms/privacy_policy`))

  const selfRegisterCreatePageNav = () => navigate(addUtmParameter(`/coupon/self_register/create`))

  const searchPageNav = () => navigate(addUtmParameter(`/search/main`))

  const searchResultPageNav = (word: string, replace?: boolean) =>
    navigate(addUtmParameter(`/search/result/${word}`), { replace: replace || false })

  const myPageNav = () => navigate(addUtmParameter(`/mypage`))

  const selfRegisterListPageNav = (replace: boolean) =>
    navigate(addUtmParameter(`/coupon/self_register`), { replace })

  const qnaPageNav = () => navigate(addUtmParameter(`/help_center/qna`))

  const couponPurchaseCancelCompletePageNav = (
    idx: number,
    couponNumber: string,
    orderId: string,
  ) => navigate(addUtmParameter(`/coupon/purchase_cancel/0/${idx}/${couponNumber}/${orderId}`))

  const couponPurchaseRefundCompletePageNav = (
    idx: number,
    couponNumber: string,
    orderId: string,
  ) =>
    navigate(addUtmParameter(`/coupon/refund_complete/0/${idx}/${couponNumber}/${orderId}`), {
      replace: true,
    })

  const couponPaymentCancelPageNav = (
    couponType: string,
    idx: string,
    couponNumber: string,
    orderId: string,
  ) =>
    navigate(
      addUtmParameter(`/coupon/payment_cancel/${couponType}/${idx}/${couponNumber}/${orderId}`),
      {
        replace: true,
      },
    )

  const couponPaymentResultPageNav = (resultUrl: string, orderId: string) =>
    navigate(addUtmParameter(`${resultUrl}/${orderId}`))

  const giftPaymentCancelPageNav = (
    couponType: string,
    idx: string,
    couponNumber: string,
    orderId: string,
  ) =>
    navigate(
      addUtmParameter(`/gift/payment_cancel/${couponType}/${idx}/${couponNumber}/${orderId}`),
      {
        replace: true,
      },
    )

  const giftSendPurchasedCompletePageNav = (
    couponType: string,
    idx: string,
    couponNumber: string,
    orderId: string,
  ) =>
    navigate(
      addUtmParameter(`/gift/send_complete/${couponType}/${idx}/${couponNumber}/${orderId}`),
      {
        replace: true,
      },
    )

  const giftcardPageNav = (requestId: string, giftType: GiftType, giftIdx: number) =>
    navigate(addUtmParameter(`/giftcard/${requestId}/${giftType}/${giftIdx}`), {
      replace: true,
    })

  const giftHotDealPageNav = () => navigate(addUtmParameter(`/giftcard/hot_deal`))

  const giftHotDealDetailPageNav = (couponIdx: number) =>
    navigate(addUtmParameter(`/giftcard/hot_deal/${couponIdx}`))

  const giftRefundApplyPageNav = (requestId: string, giftType: GiftType, giftIdx: number) =>
    navigate(addUtmParameter(`/giftcard/refund_apply/${requestId}/${giftType}/${giftIdx}`))

  const couponMapPageNav = () => navigate(addUtmParameter(`/map`))

  return {
    searchResultPageNav,
    couponPaymentResultPageNav,
    couponMapPageNav,
    couponPurchaseCancelCompletePageNav,
    couponPaymentCancelPageNav,
    couponPurchaseRefundCompletePageNav,
    brandListPageNav,
    hotDealDetailPageNav,
    selfRegisterListPageNav,
    myPageNav,
    alwaysCouponDetailPageNav,
    joinPageNav,
    passFormPageNav,
    brandSalePageNav,
    brandPossessionPageNav,
    purchasePageNav,
    qnaPageNav,
    freeCouponDetailPageNav,
    alwaysPageNav,
    termsPageNav,
    purchaseHistoryDetailPageNav,
    searchPageNav,
    refundPageNav,
    privacyPolicyPageNav,
    mainPageNav,
    serviceTermsPageNav,
    selfRegisterUpdatePageNav,
    couponDetailPageNav,
    couponPurchaseHistoryPageNav,
    purchaseHistorySavePageNav,
    errorPageNav,
    cancelCompletePageNav,
    selfRegisterCreatePageNav,
    navigatePrev,
    introPageNav,
    freeCouponPageNav,
    addUtmParameter,
    splashPageNav,
    giftPurchasePageNav,
    giftPaymentCancelPageNav,
    giftedCouponPageNav,
    giftedCouponDetailPageNav,
    giftSendPurchasedPageNav,
    giftSendPurchasedCompletePageNav,
    giftHotDealPageNav,
    giftHotDealDetailPageNav,
    giftRefundApplyPageNav,
    giftcardPageNav,
  }
}
export default usePageNav
